import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { referralAPI } from '../services/api'
import { RootState } from './store'

export type InvitationStatus = 'sent' | 'opened' | 'signed_up' | 'bounced'
export type ReferralStatus = 'pending' | 'completed' | 'credited'

export interface Invitation {
  invitationId: string
  refereeEmail: string
  status: InvitationStatus
  sentAt: string
  openedAt?: string
  signedUpAt?: string
}

export interface ReferralRecord {
  refereeEmail: string
  status: ReferralStatus
  creditsAwarded: number
  createdAt: string
  completedAt?: string
}

export interface ReferralStats {
  totalReferrals: number
  completedReferrals: number
  pendingReferrals: number
  totalCreditsEarned: number
}

export interface ValidationResult {
  valid: boolean
  reward: number | null
  message: string
}

interface ReferralState {
  code: string | null
  link: string | null
  stats: ReferralStats | null
  referrals: ReferralRecord[]
  invitations: Invitation[]
  sendingInvitation: boolean
  invitationError: string | null
  validationResult: ValidationResult | null
  validating: boolean
  loading: boolean
  error: string | null
}

const initialState: ReferralState = {
  code: null,
  link: null,
  stats: null,
  referrals: [],
  invitations: [],
  sendingInvitation: false,
  invitationError: null,
  validationResult: null,
  validating: false,
  loading: false,
  error: null
}

// Async thunks
export const fetchReferralData = createAsyncThunk(
  'referral/fetchReferralData',
  async () => {
    const response = await referralAPI.getReferralData()
    return response
  }
)

export const validateReferralCode = createAsyncThunk(
  'referral/validateCode',
  async (code: string) => {
    const response = await referralAPI.validateCode(code)
    return response
  }
)

export const sendReferralInvitation = createAsyncThunk(
  'referral/sendInvitation',
  async ({ email, name }: { email: string; name?: string }) => {
    const response = await referralAPI.sendInvitation(email, name)
    return response
  }
)

export const fetchInvitations = createAsyncThunk(
  'referral/fetchInvitations',
  async () => {
    const response = await referralAPI.getInvitations()
    return response.invitations
  }
)

const referralSlice = createSlice({
  name: 'referral',
  initialState,
  reducers: {
    clearInvitationError: (state) => {
      state.invitationError = null
    },
    clearValidationResult: (state) => {
      state.validationResult = null
    },
    resetReferralState: (state) => {
      return initialState
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch referral data
      .addCase(fetchReferralData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchReferralData.fulfilled, (state, action) => {
        state.loading = false
        state.code = action.payload.code
        state.link = action.payload.link
        state.stats = action.payload.stats
        state.referrals = action.payload.referrals
      })
      .addCase(fetchReferralData.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch referral data'
      })

      // Validate referral code
      .addCase(validateReferralCode.pending, (state) => {
        state.validating = true
        state.validationResult = null
      })
      .addCase(validateReferralCode.fulfilled, (state, action) => {
        state.validating = false
        state.validationResult = action.payload
      })
      .addCase(validateReferralCode.rejected, (state, action) => {
        state.validating = false
        state.validationResult = {
          valid: false,
          reward: null,
          message: action.error.message || 'Failed to validate code'
        }
      })

      // Send invitation
      .addCase(sendReferralInvitation.pending, (state) => {
        state.sendingInvitation = true
        state.invitationError = null
      })
      .addCase(sendReferralInvitation.fulfilled, (state, action) => {
        state.sendingInvitation = false
        // Refresh invitations list after sending
        // The invitation will be added when fetchInvitations is called
      })
      .addCase(sendReferralInvitation.rejected, (state, action) => {
        state.sendingInvitation = false
        state.invitationError = action.error.message || 'Failed to send invitation'
      })

      // Fetch invitations
      .addCase(fetchInvitations.fulfilled, (state, action) => {
        state.invitations = action.payload
      })
  }
})

export const {
  clearInvitationError,
  clearValidationResult,
  resetReferralState
} = referralSlice.actions

export default referralSlice.reducer

// Selectors
export const selectReferralCode = (state: RootState) => state.referral.code
export const selectReferralLink = (state: RootState) => state.referral.link
export const selectReferralStats = (state: RootState) => state.referral.stats
export const selectReferrals = (state: RootState) => state.referral.referrals
export const selectInvitations = (state: RootState) => state.referral.invitations
export const selectSendingInvitation = (state: RootState) => state.referral.sendingInvitation
export const selectInvitationError = (state: RootState) => state.referral.invitationError
export const selectValidationResult = (state: RootState) => state.referral.validationResult
export const selectValidating = (state: RootState) => state.referral.validating
export const selectReferralLoading = (state: RootState) => state.referral.loading
export const selectReferralError = (state: RootState) => state.referral.error
